import { jwtDecode } from "jwt-decode";
import { axiosInstanceWebsite, axiosInstanceWebsitePublic } from "./instance";
import {
  clearDelegatedAccessToken,
  getDelegatedAccessToken,
  storeDelegatedAccessToken,
} from "./authStorage";

export interface SupportTestSession {
  id: string;
  targetUserId: string;
  targetEmail: string;
  actorId: string;
  actorEmail: string;
  actorType: "HUMAN" | "AI" | string;
  agentName?: string | null;
  environment: string;
  mode: "UI_TEST" | "READ_ONLY";
  scopes: string[];
  reason: string;
  testRunId?: string | null;
  status: "ACTIVE" | "REVOKED" | "EXPIRED";
  expiresAt: string;
  createdAt: string;
}

interface ExchangeResponse {
  accessToken: string;
  session: SupportTestSession;
}

interface LaunchCodeResponse {
  sessionId: string;
  launchUrl: string;
  launchCodeExpiresAt: string;
}

const STORAGE_KEY = "fluxi_support_session";

export async function exchangeSupportLaunchCode(code: string): Promise<ExchangeResponse> {
  const { data } = await axiosInstanceWebsitePublic.post<ExchangeResponse>(
    "/support-test-sessions/exchange",
    { code }
  );
  return data;
}

export async function createSupportLaunchCode(
  entryPath: string,
  surface: "WEBSITE" | "BUILDER"
): Promise<LaunchCodeResponse> {
  const { data } = await axiosInstanceWebsite.post<LaunchCodeResponse>(
    "/support-test-sessions/current/launch-code",
    { entryPath, surface }
  );
  return data;
}

export async function getCurrentSupportSession(): Promise<SupportTestSession> {
  const { data } = await axiosInstanceWebsite.get<SupportTestSession>(
    "/support-test-sessions/current"
  );
  return data;
}

export function storeSupportSession(session: SupportTestSession) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearSupportSession() {
  sessionStorage.removeItem(STORAGE_KEY);
  clearDelegatedAccessToken();
}

export function readSupportSession(): SupportTestSession | null {
  const token = getDelegatedAccessToken();
  const serialized = sessionStorage.getItem(STORAGE_KEY);
  if (!token || !serialized) return null;

  try {
    const claims = jwtDecode<{ support_session_id?: string; exp?: number }>(token);
    const session = JSON.parse(serialized) as SupportTestSession;
    if (
      !claims.support_session_id ||
      claims.support_session_id !== session.id ||
      (claims.exp && claims.exp <= Date.now() / 1000)
    ) {
      clearSupportSession();
      return null;
    }
    return session;
  } catch {
    clearSupportSession();
    return null;
  }
}

export function storeDelegatedSupportAccessToken(token: string) {
  storeDelegatedAccessToken(token);
}

export function isDelegatedSupportToken(token: string | null): boolean {
  if (!token) return false;
  try {
    return Boolean(jwtDecode<{ support_session_id?: string }>(token).support_session_id);
  } catch {
    return false;
  }
}
